import React, { useEffect, useState } from 'react';
import { contactApi } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { DownloadIcon } from '../../components/icons';

interface ContactMessage {
  _id: string;
  name: string;
  email?: string;
  mobile?: string;
  course: string;
  message: string;
  createdAt: string;
}

const ManageContactsPage: React.FC = () => {
  const { colors } = useTheme();
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    contactApi.getAll()
      .then(res => {
        console.log('Contact API response:', res.data);
        setContacts(res.data.data.messages || []);
      })
      .catch((err) => {
        console.error('Error loading contacts:', err);
        setError(err.response?.data?.message || 'Failed to load contact messages.');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    try {
      const response = await contactApi.exportToExcel();
      
      // Create a blob from the response
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Create a download link and click it
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contact_messages_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting contacts:', err);
      setError('Failed to export contacts. Please try again.');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ color: colors.text.primary }}>Contact Messages</h2>
        {contacts.length > 0 && (
          <button
            onClick={handleExport}
            className="px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:scale-105 flex items-center gap-2"
            style={{
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
              color: colors.primary.contrastText
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
            }}
          >
            <DownloadIcon className="w-5 h-5" />
            Export to Excel
          </button>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
        </div>
      ) : error ? (
        <div style={{ color: colors.error.main }}>{error}</div>
      ) : contacts.length === 0 ? (
        <div style={{ color: colors.text.secondary }}>No contact messages found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full rounded-lg" style={{ backgroundColor: colors.background.paper }}>
            <thead>
              <tr style={{ backgroundColor: colors.background.default }}>
                <th className="px-4 py-2 text-left" style={{ color: colors.text.primary }}>Name</th>
                <th className="px-4 py-2 text-left" style={{ color: colors.text.primary }}>Course</th>
                <th className="px-4 py-2 text-left" style={{ color: colors.text.primary }}>Mobile</th>
                <th className="px-4 py-2 text-left" style={{ color: colors.text.primary }}>Email</th>
                <th className="px-4 py-2 text-left" style={{ color: colors.text.primary }}>Message</th>
                <th className="px-4 py-2 text-left" style={{ color: colors.text.primary }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c._id} className="border-b" style={{ borderColor: colors.border }}>
                  <td className="px-4 py-2 font-semibold" style={{ color: colors.text.primary }}>{c.name}</td>
                  <td className="px-4 py-2" style={{ color: colors.text.primary }}>{c.course}</td>
                  <td className="px-4 py-2" style={{ color: colors.text.secondary }}>{c.mobile || '-'}</td>
                  <td className="px-4 py-2" style={{ color: colors.text.secondary }}>{c.email || '-'}</td>
                  <td className="px-4 py-2 max-w-xs break-words" style={{ color: colors.text.secondary }}>{c.message || '-'}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: colors.text.disabled }}>{new Date(c.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageContactsPage; 